import {
	DEFAULT_EXTERNAL_VALIDATION_TABS,
	DEFAULT_WORKSPACE_TABS,
	resolveWorkspaceTabs,
	type WorkspaceTabConfig,
} from '@atlas/models'
import { ValidationLane, WorkspaceCode } from '@atlas/types'
import { AddAffordance } from '@atlas/ui-components'
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import { humanizeCode } from '@/lib/labels'

const VALIDATION_LANE_BY_STATUS_CODE: Readonly<Record<string, ValidationLane>> = {
	internal_validation: ValidationLane.INTERNAL,
	external_validation: ValidationLane.COMPLIANCE,
}

const resolveValidationLane = (statusCode: string | undefined): ValidationLane | null =>
	statusCode ? (VALIDATION_LANE_BY_STATUS_CODE[statusCode] ?? null) : null

/**
 * Workspace codes whose component reads `options.tabs` — the only ones a
 * tab editor makes sense for. Grows one entry per adoption change.
 */
export const ADOPTED_SECTION_WORKSPACE_CODES: readonly string[] = [
	WorkspaceCode.QUEUE_CARD,
	WorkspaceCode.RESOLUTION,
	WorkspaceCode.PRODUCTION,
	WorkspaceCode.VALIDATION,
	WorkspaceCode.SUMMARY,
]

const move = (list: WorkspaceTabConfig[], from: number, to: number): WorkspaceTabConfig[] => {
	if (to < 0 || to >= list.length) return list
	const next = [...list]
	const [item] = next.splice(from, 1)
	next.splice(to, 0, item)
	return next
}

/**
 * Add/remove/reorder editor for a workspace assignment's `options.tabs`. The
 * list it edits is the EFFECTIVE one — the authored list when present, the
 * workspace's default otherwise — so the first edit materializes the default
 * as authored data rather than starting from empty. Sections travel with
 * their tab; a removed tab returns to the addable pool with its default
 * composition intact. (The Checklist tab is never listed here: it derives
 * from the transition's pre-hooks.)
 */
export function WorkspaceTabsEditor({
	workspaceCode,
	options,
	statusCode,
	onChangeTabs,
}: {
	workspaceCode: WorkspaceCode
	options: Record<string, unknown>
	statusCode?: string
	onChangeTabs: (tabs: WorkspaceTabConfig[]) => void
}) {
	const lane =
		workspaceCode === WorkspaceCode.VALIDATION
			? ((options.lane as ValidationLane | undefined) ?? resolveValidationLane(statusCode))
			: null
	const defaultTabs =
		lane === ValidationLane.COMPLIANCE ? DEFAULT_EXTERNAL_VALIDATION_TABS : DEFAULT_WORKSPACE_TABS[workspaceCode]
	const effectiveOptions =
		lane === ValidationLane.COMPLIANCE && !Array.isArray(options.tabs)
			? { ...options, tabs: [...defaultTabs] }
			: options

	const tabs = [...resolveWorkspaceTabs(workspaceCode, effectiveOptions)]
	const present = new Set(tabs.map((tab) => tab.key))
	const addable = defaultTabs.filter((tab) => !present.has(tab.key))

	return (
		<div className="mt-3">
			<label className="mb-1 block font-mono text-ds-4xs uppercase tracking-wide text-ds-text-subtle">Tabs</label>
			<div className="flex flex-col gap-1">
				{tabs.map((tab, index) => (
					<div key={tab.key} className="rounded-ds-md border border-ds-border px-2 py-1.5">
						<div className="flex items-center gap-1.5">
							<span className="flex-1 text-xs font-medium text-ds-text">{tab.label}</span>
							<button
								type="button"
								aria-label={`Move ${tab.label} tab up`}
								onClick={() => onChangeTabs(move(tabs, index, index - 1))}
								className="text-ds-text-subtle disabled:opacity-40"
								disabled={index === 0}
							>
								<ArrowUp className="h-3.5 w-3.5" />
							</button>
							<button
								type="button"
								aria-label={`Move ${tab.label} tab down`}
								onClick={() => onChangeTabs(move(tabs, index, index + 1))}
								className="text-ds-text-subtle disabled:opacity-40"
								disabled={index === tabs.length - 1}
							>
								<ArrowDown className="h-3.5 w-3.5" />
							</button>
							<button
								type="button"
								aria-label={`Remove ${tab.label} tab`}
								onClick={() => onChangeTabs(tabs.filter((_, i) => i !== index))}
								className="text-ds-text-subtle disabled:opacity-40"
								disabled={tabs.length === 1}
							>
								<Trash2 className="h-3.5 w-3.5" />
							</button>
						</div>
						<p className="mt-0.5 font-mono text-ds-4xs text-ds-text-subtle">
							{tab.sections.map((section) => humanizeCode(section.code)).join(' · ')}
						</p>
					</div>
				))}
			</div>
			{addable.length > 0 ? (
				<div className="mt-2 flex flex-wrap gap-1.5">
					{addable.map((tab) => (
						<AddAffordance
							key={tab.key}
							size="sm"
							label={tab.label}
							className="rounded-ds-full px-2.5 font-mono text-ds-3xs"
							onPress={() => onChangeTabs([...tabs, structuredClone(tab) as WorkspaceTabConfig])}
						/>
					))}
				</div>
			) : null}
		</div>
	)
}
